import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// ─── Helper: parse JSON string safely ─────────────────────────────────────────
function safeParse(jsonStr, fallback = {}) {
  if (!jsonStr) return fallback;
  if (typeof jsonStr === 'object') return jsonStr;
  try { return JSON.parse(jsonStr); } catch { return fallback; }
}

// ─── Helper: format date ──────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Score color helper ───────────────────────────────────────────────────────
function scoreColor(score) {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#eab308';
  return '#ef4444';
}

function scoreLabel(score) {
  if (score >= 80) return 'Compliant';
  if (score >= 60) return 'Needs Attention';
  return 'At Risk';
}

function scoreBadgeClass(score) {
  if (score >= 80) return 'badge success';
  if (score >= 60) return 'badge warning';
  return 'badge danger';
}

// ─── SVG Circular Score ───────────────────────────────────────────────────────
function CircularScore({ score, size = 160, strokeWidth = 12 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, score || 0));
  const offset = circumference - (pct / 100) * circumference;
  const color = scoreColor(pct);

  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text
        x={size / 2}
        y={size / 2 - 6}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: size * 0.25, fontWeight: 700, fill: color }}
      >
        {pct}
      </text>
      <text
        x={size / 2}
        y={size / 2 + 20}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 12, fill: '#6b7280' }}
      >
        / 100
      </text>
    </svg>
  );
}

// ─── Component Score Bar ──────────────────────────────────────────────────────
function ScoreBar({ value, max = 100 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={styles.scoreBarTrack}>
      <div style={{ ...styles.scoreBarFill, width: `${pct}%`, backgroundColor: scoreColor(value) }} />
    </div>
  );
}

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'infrastructure', label: 'Infrastructure' },
  { key: 'risk', label: 'Risk Assessment' },
  { key: 'training', label: 'Training' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'incidents', label: 'Incidents' },
];

const COMPONENT_WEIGHTS = [
  { key: 'platform_controls', label: 'Platform Controls', weight: '40%' },
  { key: 'risk_assessment', label: 'Risk Assessment', weight: '20%' },
  { key: 'training', label: 'Training', weight: '15%' },
  { key: 'vendor_baa', label: 'Vendor / BAA', weight: '10%' },
  { key: 'incidents', label: 'Incidents', weight: '10%' },
  { key: 'access_control', label: 'Access Control', weight: '5%' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════════════
function OverviewTab({ practiceId }) {
  const [dashboard, setDashboard] = useState(null);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashRes, ctrlRes] = await Promise.all([
        api.getComplianceDashboard(practiceId),
        api.getPracticeComplianceControls(practiceId),
      ]);
      setDashboard(dashRes.dashboard || null);
      setControls((ctrlRes.controls || []).slice(0, 10));
    } catch (err) {
      console.error('Error loading compliance overview:', err);
      setError(err.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div style={styles.loadingMsg}>Loading compliance overview...</div>;
  if (error) return <div style={styles.errorMsg}>{error}</div>;
  if (!dashboard) return <div style={styles.emptyMsg}>No compliance data available.</div>;

  const scoreVal = dashboard.score?.overall_compliance_score ?? dashboard.score?.overall_score ?? 0;
  const componentScores = safeParse(dashboard.score?.component_scores);

  const renewals = dashboard.upcoming_renewals || {};
  const hasRenewals = (renewals.training || 0) + (renewals.baas || 0) + (renewals.risk_assessment || 0) > 0;

  return (
    <div>
      {/* ── Score Card ────────────────────────────────────────── */}
      <div className="card" style={styles.scoreCard}>
        <div style={styles.scoreCardInner}>
          <CircularScore score={scoreVal} />
          <div style={styles.scoreCardInfo}>
            <h2 style={styles.scoreTitle}>Compliance Score</h2>
            <span className={scoreBadgeClass(scoreVal)} style={{ fontSize: 14 }}>
              {scoreLabel(scoreVal)}
            </span>
            <p style={styles.scoreSub}>
              Last calculated: {fmtDate(dashboard.score?.calculated_at || dashboard.score?.created_at)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Quick Stats ───────────────────────────────────────── */}
      <div style={styles.statsRow}>
        <div className="card" style={styles.statCard}>
          <div style={styles.statValue}>{dashboard.tasks?.open ?? 0}</div>
          <div style={styles.statLabel}>Open Remediation Tasks</div>
        </div>
        <div className="card" style={styles.statCard}>
          <div style={styles.statValue}>{dashboard.training?.compliance_rate ?? 0}%</div>
          <div style={styles.statLabel}>Training Compliance</div>
        </div>
        <div className="card" style={styles.statCard}>
          <div style={styles.statValue}>{dashboard.incidents?.open ?? 0}</div>
          <div style={styles.statLabel}>Active Incidents</div>
        </div>
      </div>

      {/* ── Category Breakdown ────────────────────────────────── */}
      <h3 style={styles.sectionHeading}>Category Breakdown</h3>
      <div style={styles.categoryGrid}>
        {COMPONENT_WEIGHTS.map(cw => {
          const val = componentScores[cw.key] ?? 0;
          // GH-75: when a metric has no source data, the score defaults to
          // 100 ("nothing to be non-compliant about") but the user sees 0
          // records in the underlying tab — surface the default explicitly
          // so the overall score reconciles visually.
          const source = dashboard.score?.score_sources?.[cw.key];
          const isEmptyDefault = source === 'empty_default';
          const isNoValidation = source === 'no_validation';
          return (
            <div className="card" key={cw.key} style={styles.categoryCard}>
              <div style={styles.categoryHeader}>
                <span style={styles.categoryName}>{cw.label}</span>
                <span style={{ ...styles.categoryWeight, color: scoreColor(val) }}>{Math.round(val)}</span>
              </div>
              <ScoreBar value={val} />
              <div style={styles.categoryWeightLabel}>
                Weight: {cw.weight}
                {isEmptyDefault && (
                  <span
                    title="No records exist for this metric — it defaults to 100% (nothing to be non-compliant about). Add records in the relevant tab to compute a real score."
                    style={{
                      marginLeft: 8,
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      background: '#e0e7ff',
                      color: '#4338ca',
                      letterSpacing: 0.3,
                    }}
                  >
                    NO DATA · DEFAULTS TO 100
                  </span>
                )}
                {isNoValidation && (
                  <span
                    title="No control validation run has completed yet. Run platform validation to populate this score."
                    style={{
                      marginLeft: 8,
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      background: '#fef3c7',
                      color: '#92400e',
                      letterSpacing: 0.3,
                    }}
                  >
                    NO VALIDATION RUN
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Platform Controls ─────────────────────────────────── */}
      <h3 style={styles.sectionHeading}>Platform Controls Status</h3>
      {controls.length === 0 ? (
        <div className="card" style={{ padding: 16 }}>No controls data available.</div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Control</th>
                <th>Category</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {controls.map((c, i) => (
                <tr key={c.id || i}>
                  <td>{c.control_name || c.name || '—'}</td>
                  <td>{c.category || '—'}</td>
                  <td>
                    <span className={
                      c.status === 'PASS' ? 'badge success'
                        : c.status === 'FAIL' ? 'badge danger'
                          : 'badge warning'
                    }>
                      {c.status || 'UNKNOWN'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Upcoming Renewals ─────────────────────────────────── */}
      <h3 style={styles.sectionHeading}>
        Upcoming Renewals
        {hasRenewals && <span style={styles.warningIcon}> !</span>}
      </h3>
      <div style={styles.statsRow}>
        <div className="card" style={styles.renewalCard}>
          <div style={styles.renewalValue}>{renewals.training || 0}</div>
          <div style={styles.renewalLabel}>Training Expirations</div>
        </div>
        <div className="card" style={styles.renewalCard}>
          <div style={styles.renewalValue}>{renewals.baas || 0}</div>
          <div style={styles.renewalLabel}>BAA Renewals</div>
        </div>
        <div className="card" style={styles.renewalCard}>
          <div style={styles.renewalValue}>{renewals.risk_assessment || 0}</div>
          <div style={styles.renewalLabel}>Risk Assessments Due</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RISK ASSESSMENT TAB
// ═══════════════════════════════════════════════════════════════════════════════
function RiskTab({ practiceId }) {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getRiskAssessments(practiceId);
      setAssessments(res.assessments || []);
    } catch (err) {
      console.error('Error loading risk assessments:', err);
      setError(err.message || 'Failed to load risk assessments');
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    try {
      setCreating(true);
      const res = await api.createRiskAssessment(practiceId, {
        assessment_year: new Date().getFullYear(),
      });
      if (res.assessment && res.assessment.id) {
        navigate(`/practice-view/compliance/risk-assessment/${res.assessment.id}`);
      } else {
        await loadData();
      }
    } catch (err) {
      console.error('Error creating risk assessment:', err);
      alert('Failed to create risk assessment: ' + (err.message || 'Unknown error'));
    } finally {
      setCreating(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      NOT_STARTED: 'badge',
      IN_PROGRESS: 'badge info',
      COMPLETED: 'badge success',
      EXPIRED: 'badge danger',
    };
    return map[status] || 'badge';
  };

  if (loading) return <div style={styles.loadingMsg}>Loading risk assessments...</div>;
  if (error) return <div style={styles.errorMsg}>{error}</div>;

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={styles.sectionHeading}>Risk Assessments</h3>
        <button className="button" onClick={handleCreate} disabled={creating}>
          {creating ? 'Creating...' : 'New Risk Assessment'}
        </button>
      </div>
      {assessments.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          No risk assessments found. Click "New Risk Assessment" to start one.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Type</th>
                <th>Status</th>
                <th>Score</th>
                <th>Completion %</th>
                <th>Created At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((a, i) => (
                <tr
                  key={a.id || i}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/practice-view/compliance/risk-assessment/${a.id}`)}
                >
                  <td>{a.assessment_year || '—'}</td>
                  <td>{a.assessment_type || 'Standard'}</td>
                  <td><span className={statusBadge(a.status)}>{a.status || 'NOT_STARTED'}</span></td>
                  <td>{a.inherent_risk_score != null ? a.inherent_risk_score : '—'}</td>
                  <td>{a.completion_percentage != null ? `${a.completion_percentage}%` : '—'}</td>
                  <td>{fmtDate(a.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="button"
                        style={{ fontSize: 12, padding: '4px 12px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/practice-view/compliance/risk-assessment/${a.id}`);
                        }}
                      >
                        {a.status === 'COMPLETED' ? 'View Results' : a.status === 'IN_PROGRESS' ? 'Continue' : 'Start Assessment'}
                      </button>
                      {/* GH-77: delete button for assessments created in error or as test data */}
                      <button
                        className="button"
                        style={{ fontSize: 12, padding: '4px 10px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                        title="Delete this risk assessment"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const label = `${a.assessment_year || ''} ${a.assessment_type || ''}`.trim() || `id ${a.id}`;
                          if (!window.confirm(`Delete risk assessment "${label}"? This cannot be undone.`)) return;
                          try {
                            await api.deleteRiskAssessment(practiceId, a.id);
                            const refreshed = await api.getRiskAssessments(practiceId);
                            setAssessments(refreshed.assessments || []);
                          } catch (err) {
                            alert(`Delete failed: ${err.message || 'unknown error'}`);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TRAINING TAB
// ═══════════════════════════════════════════════════════════════════════════════
function TrainingTab({ practiceId }) {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    staff_user_id: '',
    training_name: '',
    training_type: 'HIPAA_ANNUAL',
    training_date: new Date().toISOString().split('T')[0],
    training_provider: '',
    expiration_date: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getTrainingRecords(practiceId);
      setRecords(res.records || []);
      setStats(res.stats || null);
    } catch (err) {
      console.error('Error loading training records:', err);
      setError(err.message || 'Failed to load training records');
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateTraining = async () => {
    if (!form.staff_user_id || !form.training_name || !form.training_date) {
      alert('Please fill in Staff User ID, Training Name, and Training Date.');
      return;
    }
    try {
      setSaving(true);
      await api.createTrainingRecord(practiceId, {
        staff_user_id: parseInt(form.staff_user_id),
        training_name: form.training_name,
        training_type: form.training_type,
        training_date: form.training_date,
        training_provider: form.training_provider || undefined,
        expiration_date: form.expiration_date || undefined,
      });
      setShowForm(false);
      setForm({
        staff_user_id: '',
        training_name: '',
        training_type: 'HIPAA_ANNUAL',
        training_date: new Date().toISOString().split('T')[0],
        training_provider: '',
        expiration_date: '',
      });
      await loadData();
    } catch (err) {
      console.error('Error creating training record:', err);
      alert('Failed to create training record: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const trainingBadge = (status) => {
    const map = {
      COMPLETED: 'badge success',
      UPCOMING: 'badge info',
      EXPIRED: 'badge danger',
    };
    return map[status] || 'badge';
  };

  if (loading) return <div style={styles.loadingMsg}>Loading training records...</div>;
  if (error) return <div style={styles.errorMsg}>{error}</div>;

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={styles.sectionHeading}>Training Compliance</h3>
        <button className="button" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Add Training Record'}
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#111827' }}>New Training Record</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={styles.formLabel}>Staff User ID *</label>
              <input type="number" value={form.staff_user_id} onChange={e => handleFormChange('staff_user_id', e.target.value)} style={styles.formInput} placeholder="User ID" />
            </div>
            <div>
              <label style={styles.formLabel}>Training Name *</label>
              <input type="text" value={form.training_name} onChange={e => handleFormChange('training_name', e.target.value)} style={styles.formInput} placeholder="e.g. HIPAA Annual Training 2026" />
            </div>
            <div>
              <label style={styles.formLabel}>Training Type</label>
              <select value={form.training_type} onChange={e => handleFormChange('training_type', e.target.value)} style={styles.formInput}>
                <option value="HIPAA_ANNUAL">HIPAA Annual</option>
                <option value="SECURITY_AWARENESS">Security Awareness</option>
                <option value="BREACH_NOTIFICATION">Breach Notification</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>
            <div>
              <label style={styles.formLabel}>Training Date *</label>
              <input type="date" value={form.training_date} onChange={e => handleFormChange('training_date', e.target.value)} style={styles.formInput} />
            </div>
            <div>
              <label style={styles.formLabel}>Training Provider</label>
              <input type="text" value={form.training_provider} onChange={e => handleFormChange('training_provider', e.target.value)} style={styles.formInput} placeholder="Optional" />
            </div>
            <div>
              <label style={styles.formLabel}>Expiration Date</label>
              <input type="date" value={form.expiration_date} onChange={e => handleFormChange('expiration_date', e.target.value)} style={styles.formInput} />
            </div>
          </div>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button className="button" onClick={handleCreateTraining} disabled={saving}>
              {saving ? 'Saving...' : 'Save Training Record'}
            </button>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      {stats && (
        <div style={styles.statsRow}>
          <div className="card" style={styles.statCard}>
            <div style={styles.statValue}>{stats.total_staff || records.length}</div>
            <div style={styles.statLabel}>Total Staff</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statValue}>{stats.trained || 0}</div>
            <div style={styles.statLabel}>Trained</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statValue}>{stats.expiring_soon || 0}</div>
            <div style={styles.statLabel}>Expiring Soon</div>
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          No training records found. Click "Add Training Record" to create one.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Training Name</th>
                <th>Training Type</th>
                <th>Status</th>
                <th>Training Date</th>
                <th>Expiration Date</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r.id || i}>
                  <td>{r.training_name || '—'}</td>
                  <td>{r.training_type || '—'}</td>
                  <td><span className={trainingBadge(r.status)}>{r.status || '—'}</span></td>
                  <td>{fmtDate(r.training_date)}</td>
                  <td>{fmtDate(r.expiration_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VENDORS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function VendorsTab({ practiceId }) {
  const [vendors, setVendors] = useState([]);
  const [stats, setStats] = useState(null);
  const [complianceRate, setComplianceRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendor_name: '',
    vendor_contact_name: '',
    vendor_contact_email: '',
    risk_classification: 'LOW',
    handles_phi: false,
    baa_signed: false,
    baa_expiration_date: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getComplianceVendors(practiceId);
      setVendors(res.vendors || []);
      setStats(res.stats || null);
      setComplianceRate(res.compliance_rate != null ? res.compliance_rate : null);
    } catch (err) {
      console.error('Error loading vendors:', err);
      setError(err.message || 'Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    if (!form.vendor_name.trim()) return;
    try {
      setSaving(true);
      await api.createComplianceVendor(practiceId, form);
      setShowForm(false);
      setForm({
        vendor_name: '',
        vendor_contact_name: '',
        vendor_contact_email: '',
        risk_classification: 'LOW',
        handles_phi: false,
        baa_signed: false,
        baa_expiration_date: '',
      });
      await loadData();
    } catch (err) {
      console.error('Error adding vendor:', err);
      alert('Failed to add vendor: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const riskBadge = (level) => {
    const map = { HIGH: 'badge danger', MEDIUM: 'badge warning', LOW: 'badge success' };
    return map[level] || 'badge';
  };


  if (loading) return <div style={styles.loadingMsg}>Loading vendors...</div>;
  if (error) return <div style={styles.errorMsg}>{error}</div>;

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={styles.sectionHeading}>Vendor Management</h3>
        <button className="button" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Add Vendor'}
        </button>
      </div>

      {/* Stats Bar */}
      <div style={styles.statsRow}>
        <div className="card" style={styles.statCard}>
          <div style={styles.statValue}>{stats?.total || vendors.length}</div>
          <div style={styles.statLabel}>Total Vendors</div>
        </div>
        <div className="card" style={styles.statCard}>
          <div style={styles.statValue}>{stats?.with_baa || 0}</div>
          <div style={styles.statLabel}>With BAA</div>
        </div>
        <div className="card" style={styles.statCard}>
          <div style={styles.statValue}>{complianceRate != null ? `${complianceRate}%` : '—'}</div>
          <div style={styles.statLabel}>Compliance Rate</div>
        </div>
      </div>

      {/* Inline Add Vendor Form */}
      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15 }}>Add New Vendor</h4>
          <form onSubmit={handleAddVendor}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Vendor Name *</label>
                <input
                  style={styles.formInput}
                  value={form.vendor_name}
                  onChange={e => handleFormChange('vendor_name', e.target.value)}
                  placeholder="Vendor name"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Contact Name</label>
                <input
                  style={styles.formInput}
                  value={form.vendor_contact_name}
                  onChange={e => handleFormChange('vendor_contact_name', e.target.value)}
                  placeholder="Contact person"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Contact Email</label>
                <input
                  style={styles.formInput}
                  type="email"
                  value={form.vendor_contact_email}
                  onChange={e => handleFormChange('vendor_contact_email', e.target.value)}
                  placeholder="email@vendor.com"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Risk Classification</label>
                <select
                  style={styles.formInput}
                  value={form.risk_classification}
                  onChange={e => handleFormChange('risk_classification', e.target.value)}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
              </div>
              <div style={{ ...styles.formGroup, display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                <input
                  type="checkbox"
                  checked={form.baa_signed}
                  onChange={e => handleFormChange('baa_signed', e.target.checked)}
                  id="baa_signed"
                />
                <label htmlFor="baa_signed" style={{ fontSize: 13, cursor: 'pointer' }}>BAA Signed</label>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>BAA Expiration</label>
                <input
                  style={styles.formInput}
                  type="date"
                  value={form.baa_expiration_date}
                  onChange={e => handleFormChange('baa_expiration_date', e.target.value)}
                />
              </div>
              <div style={{ ...styles.formGroup, display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                <input
                  type="checkbox"
                  checked={form.handles_phi}
                  onChange={e => handleFormChange('handles_phi', e.target.checked)}
                  id="handles_phi"
                />
                <label htmlFor="handles_phi" style={{ fontSize: 13, cursor: 'pointer' }}>Handles PHI</label>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="button" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Vendor'}
              </button>
              <button className="button secondary" type="button" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Vendor Table */}
      {vendors.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          No vendors found. Click "Add Vendor" to add one.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Vendor Name</th>
                <th>Contact</th>
                <th>Risk</th>
                <th>Handles PHI</th>
                <th>BAA Status</th>
                <th>BAA Expiration</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v, i) => (
                <tr key={v.id || i}>
                  <td style={{ fontWeight: 500 }}>{v.vendor_name || '—'}</td>
                  <td>
                    <div>{v.vendor_contact_name || '—'}</div>
                    {v.vendor_contact_email && <div style={{ fontSize: 12, color: '#6b7280' }}>{v.vendor_contact_email}</div>}
                  </td>
                  <td><span className={riskBadge(v.risk_classification)}>{v.risk_classification || '—'}</span></td>
                  <td>{v.handles_phi ? 'Yes' : 'No'}</td>
                  <td><span className={v.baa_signed ? 'badge success' : 'badge'}>{v.baa_signed ? 'Signed' : 'None'}</span></td>
                  <td>{fmtDate(v.baa_expiration_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INCIDENTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function IncidentsTab({ practiceId }) {
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    incident_type: 'SECURITY_INCIDENT',
    incident_title: '',
    incident_description: '',
    incident_date: new Date().toISOString().split('T')[0],
    is_breach: false,
    reported_anonymously: false,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getComplianceIncidents(practiceId);
      setIncidents(res.incidents || []);
      setStats(res.stats || null);
    } catch (err) {
      console.error('Error loading incidents:', err);
      setError(err.message || 'Failed to load incident data');
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleReportIncident = async (e) => {
    e.preventDefault();
    if (!form.incident_title.trim()) return;
    try {
      setSaving(true);
      await api.createComplianceIncident(practiceId, form);
      setShowForm(false);
      setForm({
        incident_type: 'SECURITY_INCIDENT',
        incident_title: '',
        incident_description: '',
        incident_date: new Date().toISOString().split('T')[0],
        is_breach: false,
        reported_anonymously: false,
      });
      await loadData();
    } catch (err) {
      console.error('Error reporting incident:', err);
      alert('Failed to report incident: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      OPEN: 'badge warning',
      IN_PROGRESS: 'badge info',
      RESOLVED: 'badge success',
      CLOSED: 'badge',
    };
    return map[status] || 'badge';
  };

  // Use days_until_deadline from DB (recalculated daily by cron) or compute from breach_discovery_date
  const breachDaysRemaining = (incident) => {
    if (!incident.is_breach) return null;
    if (incident.days_until_deadline != null) return incident.days_until_deadline;
    if (!incident.breach_discovery_date && !incident.created_at) return null;
    const discovered = new Date(incident.breach_discovery_date || incident.created_at);
    const deadline = new Date(discovered);
    deadline.setDate(deadline.getDate() + 60);
    const now = new Date();
    const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) return <div style={styles.loadingMsg}>Loading incidents...</div>;
  if (error) return <div style={styles.errorMsg}>{error}</div>;

  return (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={styles.sectionHeading}>Incident Management</h3>
        <button className="button" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Report Incident'}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={styles.statsRow}>
          <div className="card" style={styles.statCard}>
            <div style={styles.statValue}>{stats.open || 0}</div>
            <div style={styles.statLabel}>Open Incidents</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statValue}>{stats.breach_count || 0}</div>
            <div style={styles.statLabel}>Breaches</div>
          </div>
          <div className="card" style={styles.statCard}>
            <div style={styles.statValue}>
              {stats.avg_resolution_days != null ? `${Math.round(stats.avg_resolution_days)}d` : '—'}
            </div>
            <div style={styles.statLabel}>Avg Resolution</div>
          </div>
        </div>
      )}

      {/* Inline Report Form */}
      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15 }}>Report New Incident</h4>
          <form onSubmit={handleReportIncident}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Incident Type</label>
                <select
                  style={styles.formInput}
                  value={form.incident_type}
                  onChange={e => handleFormChange('incident_type', e.target.value)}
                >
                  <option value="SECURITY_INCIDENT">Security Incident</option>
                  <option value="BREACH">Breach</option>
                  <option value="NEAR_MISS">Near Miss</option>
                  <option value="POLICY_VIOLATION">Policy Violation</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Incident Date *</label>
                <input
                  type="date"
                  style={styles.formInput}
                  value={form.incident_date}
                  onChange={e => handleFormChange('incident_date', e.target.value)}
                  required
                />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                <label style={styles.formLabel}>Title *</label>
                <input
                  type="text"
                  style={styles.formInput}
                  value={form.incident_title}
                  onChange={e => handleFormChange('incident_title', e.target.value)}
                  placeholder="Brief title for the incident"
                  required
                />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                <label style={styles.formLabel}>Description</label>
                <textarea
                  style={{ ...styles.formInput, minHeight: 80, resize: 'vertical' }}
                  value={form.incident_description}
                  onChange={e => handleFormChange('incident_description', e.target.value)}
                  placeholder="Describe the incident..."
                />
              </div>
              <div style={{ ...styles.formGroup, display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4 }}>
                <input
                  type="checkbox"
                  checked={form.is_breach}
                  onChange={e => handleFormChange('is_breach', e.target.checked)}
                  id="is_breach"
                />
                <label htmlFor="is_breach" style={{ fontSize: 13, cursor: 'pointer' }}>
                  This is a breach (triggers 60-day notification rule)
                </label>
              </div>
              <div style={{ ...styles.formGroup, display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4 }}>
                <input
                  type="checkbox"
                  checked={form.reported_anonymously}
                  onChange={e => handleFormChange('reported_anonymously', e.target.checked)}
                  id="reported_anonymously"
                />
                <label htmlFor="reported_anonymously" style={{ fontSize: 13, cursor: 'pointer' }}>
                  Report anonymously
                </label>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="button" type="submit" disabled={saving}>
                {saving ? 'Submitting...' : 'Submit Incident'}
              </button>
              <button className="button secondary" type="button" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Incidents Table */}
      {incidents.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          No incidents reported. Click "Report Incident" to file one.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Is Breach</th>
                <th>Days Remaining</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc, i) => {
                const daysLeft = breachDaysRemaining(inc);
                return (
                  <tr key={inc.id || i}>
                    <td>{fmtDate(inc.incident_date || inc.created_at)}</td>
                    <td>{inc.incident_title || '—'}</td>
                    <td>{inc.incident_type || '—'}</td>
                    <td><span className={statusBadge(inc.status)}>{inc.status || '—'}</span></td>
                    <td>
                      {inc.is_breach
                        ? <span className="badge danger">Breach</span>
                        : <span style={{ color: '#9ca3af' }}>No</span>
                      }
                    </td>
                    <td>
                      {inc.is_breach && daysLeft != null ? (
                        <span className={daysLeft <= 10 ? 'badge danger' : daysLeft <= 30 ? 'badge warning' : 'badge info'}>
                          {daysLeft > 0 ? `${daysLeft} days` : 'Overdue'}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INFRASTRUCTURE TAB (Super Admin — AWS compliance checks)
// ═══════════════════════════════════════════════════════════════════════════════

const INFRA_GROUPS = [
  { key: 'rds', label: 'RDS Database', codes: ['HIPAA-AWS-001', 'HIPAA-AWS-002', 'HIPAA-AWS-003', 'HIPAA-AWS-004'] },
  { key: 's3', label: 'S3 Storage', codes: ['HIPAA-AWS-005', 'HIPAA-AWS-006', 'HIPAA-AWS-007'] },
  { key: 'ecs', label: 'Compute (ECS)', codes: ['HIPAA-AWS-008'] },
  { key: 'logs', label: 'Logging', codes: ['HIPAA-AWS-009', 'HIPAA-AWS-010'] },
  { key: 'network', label: 'Network', codes: ['HIPAA-AWS-011'] },
  { key: 'creds', label: 'Credentials', codes: ['HIPAA-AWS-012'] },
];

const STATUS_BADGE = {
  PASS: { bg: '#dcfce7', text: '#166534', label: 'PASS' },
  FAIL: { bg: '#fee2e2', text: '#991b1b', label: 'FAIL' },
  WARNING: { bg: '#fef3c7', text: '#92400e', label: 'WARNING' },
  NOT_APPLICABLE: { bg: '#f3f4f6', text: '#6b7280', label: 'N/A' },
  ERROR: { bg: '#fce7f3', text: '#9d174d', label: 'ERROR' },
};

function InfrastructureTab() {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getComplianceControls({ control_category: 'Infrastructure' });
      setControls(res.controls || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runValidation = async () => {
    try {
      setRunning(true);
      await api.triggerComplianceValidation();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div style={styles.loadingMsg}>Loading infrastructure checks...</div>;
  if (error) return <div style={styles.errorMsg}>Error: {error}</div>;

  const total = controls.length;
  const passing = controls.filter(c => c.latest_status === 'PASS').length;
  const failing = controls.filter(c => c.latest_status === 'FAIL').length;
  const warnings = controls.filter(c => c.latest_status === 'WARNING').length;
  const na = controls.filter(c => c.latest_status === 'NOT_APPLICABLE' || !c.latest_status).length;
  const complianceRate = total > 0 && (total - na) > 0
    ? Math.round((passing / (total - na)) * 100)
    : (total > 0 ? 0 : 100);

  return (
    <div>
      {/* Summary Bar */}
      <div className="card" style={{ padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <CircularScore score={complianceRate} size={100} strokeWidth={10} />
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>AWS Infrastructure</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {passing} passing, {failing} failing, {warnings} warnings, {na} not applicable
            </p>
          </div>
        </div>
        <button
          className="button"
          onClick={runValidation}
          disabled={running}
          style={{ fontSize: 13, padding: '8px 16px' }}
        >
          {running ? 'Running...' : 'Run Validation'}
        </button>
      </div>

      {/* Grouped Controls */}
      {INFRA_GROUPS.map(group => {
        const groupControls = group.codes
          .map(code => controls.find(c => c.control_code === code))
          .filter(Boolean);
        if (groupControls.length === 0) return null;

        return (
          <div key={group.key} className="card" style={{ marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#374151' }}>{group.label}</h4>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={infraStyles.th}>Control</th>
                  <th style={infraStyles.th}>Status</th>
                  <th style={infraStyles.th}>Severity</th>
                  <th style={infraStyles.th}>Details</th>
                  <th style={infraStyles.th}>Last Checked</th>
                </tr>
              </thead>
              <tbody>
                {groupControls.map(ctrl => {
                  const badge = STATUS_BADGE[ctrl.latest_status] || STATUS_BADGE.NOT_APPLICABLE;
                  const evidence = safeParse(ctrl.latest_evidence);
                  const detail = ctrl.latest_failure_reason
                    || evidence?.reason
                    || (ctrl.latest_status === 'PASS' ? 'All checks passed' : '—');

                  return (
                    <tr key={ctrl.control_code} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={infraStyles.td}>
                        <div style={{ fontWeight: 500, color: '#111827' }}>{ctrl.control_name}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{ctrl.control_code}</div>
                      </td>
                      <td style={infraStyles.td}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                          fontSize: 11, fontWeight: 600, background: badge.bg, color: badge.text,
                        }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={infraStyles.td}>
                        <span style={{ fontSize: 11, fontWeight: 500, color: ctrl.severity === 'CRITICAL' ? '#dc2626' : ctrl.severity === 'HIGH' ? '#d97706' : '#6b7280' }}>
                          {ctrl.severity}
                        </span>
                      </td>
                      <td style={{ ...infraStyles.td, maxWidth: 350, whiteSpace: 'normal', lineHeight: 1.4 }}>
                        {detail.length > 120 ? detail.slice(0, 120) + '...' : detail}
                      </td>
                      <td style={infraStyles.td}>{fmtDate(ctrl.latest_checked_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const infraStyles = {
  th: {
    padding: '8px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb',
    fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: 0.5, background: '#fff',
  },
  td: { padding: '10px 16px', color: '#374151', whiteSpace: 'nowrap' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPLIANCE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function Compliance({ practiceId }) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!practiceId) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
        Please select a practice to view compliance dashboard.
      </div>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab practiceId={practiceId} />;
      case 'infrastructure':
        return <InfrastructureTab />;
      case 'risk':
        return <RiskTab practiceId={practiceId} />;
      case 'training':
        return <TrainingTab practiceId={practiceId} />;
      case 'vendors':
        return <VendorsTab practiceId={practiceId} />;
      case 'incidents':
        return <IncidentsTab practiceId={practiceId} />;
      default:
        return <OverviewTab practiceId={practiceId} />;
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Compliance Dashboard</h1>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabNav}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'button' : 'button secondary'}
            style={styles.tabButton}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active Tab Content */}
      <div style={styles.tabContent}>
        {renderTab()}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  page: {
    padding: 0,
  },
  pageHeader: {
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },

  // ── Tabs ──
  tabNav: {
    display: 'flex',
    gap: 6,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  tabButton: {
    fontSize: 13,
    padding: '7px 16px',
    borderRadius: 6,
    cursor: 'pointer',
  },
  tabContent: {
    minHeight: 200,
  },

  // ── Loading / Error / Empty ──
  loadingMsg: {
    textAlign: 'center',
    padding: 40,
    color: '#6b7280',
    fontSize: 14,
  },
  errorMsg: {
    textAlign: 'center',
    padding: 40,
    color: '#dc2626',
    fontSize: 14,
  },
  emptyMsg: {
    textAlign: 'center',
    padding: 40,
    color: '#9ca3af',
    fontSize: 14,
  },

  // ── Score Card ──
  scoreCard: {
    padding: 24,
    marginBottom: 16,
  },
  scoreCardInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 28,
    flexWrap: 'wrap',
  },
  scoreCardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  scoreTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    color: '#111827',
  },
  scoreSub: {
    margin: 0,
    fontSize: 12,
    color: '#9ca3af',
  },

  // ── Stats Row ──
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    padding: 16,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 26,
    fontWeight: 700,
    color: '#111827',
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },

  // ── Category Grid ──
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
    marginBottom: 24,
  },
  categoryCard: {
    padding: 16,
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  categoryName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
  },
  categoryWeight: {
    fontSize: 22,
    fontWeight: 700,
  },
  categoryWeightLabel: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 6,
  },

  // ── Score Bar ──
  scoreBarTrack: {
    height: 6,
    backgroundColor: '#e5e7eb',
    borderRadius: 3,
    overflow: 'hidden',
  },
  scoreBarFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.4s ease',
  },

  // ── Section ──
  sectionHeading: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
    margin: '0 0 12px',
  },

  // ── Tab header (title + action button) ──
  tabHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 8,
  },

  // ── Renewals ──
  renewalCard: {
    padding: 16,
    textAlign: 'center',
  },
  renewalValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
  },
  renewalLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  warningIcon: {
    display: 'inline-block',
    width: 22,
    height: 22,
    lineHeight: '22px',
    borderRadius: '50%',
    backgroundColor: '#fbbf24',
    color: '#78350f',
    textAlign: 'center',
    fontSize: 13,
    fontWeight: 700,
    marginLeft: 8,
    verticalAlign: 'middle',
  },

  // ── Form ──
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 12,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  formLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
  },
  formInput: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    outline: 'none',
    fontFamily: 'inherit',
  },
};

export default Compliance;
